/**
 * /admin/provider-benchmark — RentCast | RealEstateAPI | the appraiser, side by side.
 *
 * Admin only, and the backend answers 404 unless PROVIDER_BENCHMARK is on, so in
 * production this page reads "not enabled" until the flag is set. Paste one address per
 * line (or click subjects from the ground-truth list), run, and read each row across the
 * three columns: the value and the comps each source used. The appraisal column shows
 * EVERY report on file for the address — nobody picks one for you.
 *
 * RealEstateAPI is a stub until the trial is on; its column says so.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminProfitabilityAPI, providerBenchmarkAPI } from '../api';
import { toast } from 'sonner';

const money = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : `$${Math.round(Number(v)).toLocaleString()}`);
const num = (v, d = 0) => (v == null || v === '' || Number.isNaN(Number(v)) ? '—' : Number(v).toFixed(d));
const pct = (a, b) => (a && b ? `${(((a - b) / b) * 100).toFixed(1)}%` : '—');

function CompsTable({ comps }) {
  if (!comps || comps.length === 0) return <div className="text-xs text-slate-400">no comps</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px] leading-tight">
        <thead>
          <tr className="text-slate-500">
            <th className="text-left font-medium pr-2">Address</th>
            <th className="text-right font-medium pr-2">Price</th>
            <th className="text-right font-medium pr-2">Date</th>
            <th className="text-right font-medium pr-2">Dist</th>
            <th className="text-right font-medium pr-2">SqFt</th>
            <th className="text-right font-medium pr-2">$/SqFt</th>
            <th className="text-right font-medium">Bd/Ba</th>
          </tr>
        </thead>
        <tbody>
          {comps.map((c, i) => (
            <tr key={`${c.address}-${i}`} className="border-t border-slate-100 align-top">
              <td className="pr-2 py-0.5 max-w-[190px] truncate" title={c.address}>{c.address}</td>
              <td className="pr-2 py-0.5 text-right whitespace-nowrap">{money(c.sale_price)}</td>
              <td className="pr-2 py-0.5 text-right whitespace-nowrap">{c.sale_date || '—'}</td>
              <td className="pr-2 py-0.5 text-right whitespace-nowrap">{c.distance_mi != null ? `${num(c.distance_mi, 2)} mi` : '—'}</td>
              <td className="pr-2 py-0.5 text-right whitespace-nowrap">{c.sqft ? Number(c.sqft).toLocaleString() : '—'}</td>
              <td className="pr-2 py-0.5 text-right whitespace-nowrap">
                {c.ppsf != null ? `$${num(c.ppsf, 0)}` : '—'}
                {c.adjusted_ppsf != null && c.adjusted_ppsf !== c.ppsf ? <span className="text-slate-400"> →{num(c.adjusted_ppsf, 0)}</span> : null}
              </td>
              <td className="py-0.5 text-right whitespace-nowrap">{c.beds ?? '—'}/{c.baths ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RentcastCell({ data, appraisalValue }) {
  if (!data) return null;
  if (data.status !== 'ok') return <div className="text-xs text-red-600">{data.error || data.status}</div>;
  return (
    <div>
      <div className="text-lg font-bold text-slate-900">{money(data.value)} <span className="text-xs font-normal text-slate-500">ARV</span></div>
      <div className="text-xs text-slate-500 mb-2">
        {String(data.confidence || '').replace('ConfidenceLevel.', '')} {data.confidence_score != null ? `(${Math.round(data.confidence_score)}/100)` : ''}
        {' · '}pool {data.pool_size} · {data.comps?.length || 0} used, {data.comps_excluded_count} set aside
        {appraisalValue ? <span className="ml-2 font-medium text-slate-700">vs appraisal {pct(data.value, appraisalValue)}</span> : null}
      </div>
      {data.subject ? (
        <div className="text-[11px] text-slate-500 mb-1">
          subject: {data.subject.sqft ? `${Number(data.subject.sqft).toLocaleString()} sqft` : '—'} · {data.subject.beds ?? '—'}/{data.subject.baths ?? '—'} · {data.subject.property_type || '—'}
        </div>
      ) : null}
      <CompsTable comps={data.comps} />
    </div>
  );
}

function RealEstateApiCell({ data }) {
  if (!data) return null;
  if (data.status === 'stub') {
    return (
      <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-500">
        <div className="font-medium text-slate-600 mb-1">Not wired yet</div>
        {data.note}
      </div>
    );
  }
  if (data.status !== 'ok') return <div className="text-xs text-red-600">{data.error || data.status}{data.http_status ? ` (HTTP ${data.http_status})` : ''}</div>;
  return (
    <div>
      <div className="text-lg font-bold text-slate-900">{money(data.value)} <span className="text-xs font-normal text-slate-500">AVM</span></div>
      <div className="text-xs text-slate-500 mb-2">
        {data.value_low != null || data.value_high != null ? `${money(data.value_low)} – ${money(data.value_high)} · ` : ''}
        {data.record_count ?? data.comps?.length ?? 0} comps · {data.credits_estimate ?? 1} credit (1 per call)
        {data.credit_headers && Object.keys(data.credit_headers).length ? ` · ${Object.entries(data.credit_headers).map(([k, v]) => `${k}: ${v}`).join(', ')}` : ''}
      </div>
      {data.subject ? (
        <div className="text-[11px] text-slate-500 mb-1">
          subject: {data.subject.sqft ? `${Number(data.subject.sqft).toLocaleString()} sqft` : '—'} · {data.subject.beds ?? '—'}/{data.subject.baths ?? '—'} · {data.subject.property_type || '—'}
        </div>
      ) : null}
      <CompsTable comps={data.comps} />
    </div>
  );
}

function AppraisalCell({ data }) {
  if (!data) return null;
  if (data.status !== 'ok' || !data.reports?.length) {
    return <div className="text-xs text-slate-400">no appraisal on file for this address</div>;
  }
  return (
    <div className="space-y-3">
      {data.reports.map((r) => (
        <div key={r.id} className="rounded border border-slate-200 p-2">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-lg font-bold text-slate-900">
              {money(r.value)}{' '}
              <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${r.value_type === 'arv' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}`}>
                {r.value_type === 'arv' ? 'ARV' : 'AS-IS'}
              </span>
            </div>
            <div className="text-xs text-slate-500 whitespace-nowrap">{r.effective_date || '—'} · {r.property_type}{r.form ? ` · form ${r.form}` : ''}</div>
          </div>
          {r.notes ? <div className="text-[11px] text-slate-500 mb-1">{r.notes}</div> : null}
          <CompsTable comps={r.comps} />
        </div>
      ))}
    </div>
  );
}

export default function ProviderBenchmark() {
  const navigate = useNavigate();
  const [access, setAccess] = useState('checking');   // checking | ok | denied | disabled
  const [subjects, setSubjects] = useState([]);
  const [text, setText] = useState('');
  const [running, setRunning] = useState(false);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const a = await adminProfitabilityAPI.verifyAccess();
        if (!a.data?.is_admin) { setAccess('denied'); return; }
      } catch (e) { setAccess('denied'); return; }
      try {
        const s = await providerBenchmarkAPI.subjects();
        setSubjects(s.data?.appraisals || []);
        setAccess('ok');
      } catch (e) {
        setAccess(e?.response?.status === 404 ? 'disabled' : 'ok');
      }
    })();
  }, []);

  const addresses = useMemo(() => text.split('\n').map((l) => l.trim()).filter(Boolean), [text]);

  const run = async () => {
    if (addresses.length === 0) { toast.error('Paste at least one address'); return; }
    setRunning(true);
    setRows([]);
    try {
      const r = await providerBenchmarkAPI.run(addresses);
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Benchmark failed');
    } finally {
      setRunning(false);
    }
  };

  const addSubject = (s) => {
    const line = `${s.address}, ${s.city}, ${s.state}`;
    if (!addresses.includes(line)) setText((t) => (t ? `${t}\n${line}` : line));
  };

  const visibleSubjects = subjects.filter((s) => !filter || `${s.address} ${s.city}`.toLowerCase().includes(filter.toLowerCase()));

  if (access === 'checking') return <div className="p-8 text-slate-500">Checking access…</div>;
  if (access === 'denied') return <div className="p-8 text-slate-700">Admin only. <button className="underline" onClick={() => navigate('/')}>Home</button></div>;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6 max-w-[1500px]">
        <div className="flex items-baseline justify-between mb-4">
          <h1 className="text-2xl font-bold text-slate-900">Provider benchmark</h1>
          <div className="text-xs text-slate-500">RentCast · RealEstateAPI · appraiser — admin only, flag PROVIDER_BENCHMARK</div>
        </div>

        {access === 'disabled' ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            The benchmark is not enabled on this backend (PROVIDER_BENCHMARK is off). Set the flag and reload.
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Addresses — one per line (batch, up to 60)</label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={6}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                  placeholder={'187 Leroy St, Ferndale, MI 48220\n553 Belmont St, Detroit, MI 48202'}
                  data-testid="benchmark-addresses"
                />
                <div className="mt-2 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={run}
                    disabled={running || addresses.length === 0}
                    className="px-4 py-2 rounded-lg bg-secondary text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                    data-testid="benchmark-run"
                  >
                    {running ? `Running ${addresses.length}…` : `Run ${addresses.length || ''}`}
                  </button>
                  <span className="text-xs text-slate-500">Each address costs one RentCast fetch (cached 7 days). Nothing is saved as an analysis; no quota is counted.</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Ground truth on file ({subjects.length}) — click to add</label>
                <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="filter…" className="w-full rounded border border-slate-300 px-2 py-1 text-xs mb-1" />
                <div className="h-40 overflow-auto rounded border border-slate-200 bg-white text-xs">
                  {visibleSubjects.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => addSubject(s)}
                      className="w-full text-left px-2 py-1 hover:bg-blue-50 flex justify-between gap-2 border-b border-slate-100"
                    >
                      <span className="truncate">{s.address}, {s.city} {s.state}</span>
                      <span className="whitespace-nowrap text-slate-500">{s.value_type === 'arv' ? 'ARV' : 'as-is'} {money(s.value)} · {s.comps}c</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {rows.length > 0 && (
              <div className="space-y-6" data-testid="benchmark-rows">
                {rows.map((row, i) => {
                  const firstAppraisal = row.appraisal?.reports?.[0];
                  return (
                    <div key={`${row.address}-${i}`} className="rounded-xl border border-slate-200 bg-white p-4">
                      <div className="font-semibold text-slate-900 mb-3">{i + 1}. {row.address}</div>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <div className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase mb-1">RentCast (engine)</div>
                          <RentcastCell data={row.rentcast} appraisalValue={row.appraisal?.reports?.length === 1 ? firstAppraisal?.value : null} />
                        </div>
                        <div>
                          <div className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase mb-1">RealEstateAPI</div>
                          <RealEstateApiCell data={row.realestateapi} />
                        </div>
                        <div>
                          <div className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase mb-1">Appraisal</div>
                          <AppraisalCell data={row.appraisal} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
